import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import QrScanner from "qr-scanner";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { validateTicket } from "../../api/subscription";
import {
  EVENT_ADMIN,
  EVENT_VIEWER,
  TICKET_CONTROL,
  decodeAuth,
  eventIdFromPath,
  hasRoleAnywhere,
  roleFor,
} from "../DashboardPage/components/useRole";

// Catches any render-time crash on this page so it degrades to a readable
// message + reload instead of a blank white screen (there is no app-level
// error boundary). The captured message is also surfaced for debugging.
class ScanErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error("ScanPage crashed:", error, info?.componentStack);
  }

  render() {
    if (this.state.error) {
      return (
        <div className="min-h-screen bg-surface-950 text-white flex flex-col items-center justify-center text-center p-6">
          <div className="text-5xl mb-4">⚠️</div>
          <h1 className="text-lg font-bold mb-2">Səhifədə xəta baş verdi</h1>
          <p className="text-sm text-surface-400 mb-6 max-w-xs break-words">
            {String(this.state.error?.message || this.state.error)}
          </p>
          <button
            onClick={() => window.location.reload()}
            className="bg-accent-600 hover:bg-accent-700 text-white px-5 py-2.5 rounded-xl text-sm font-semibold transition-colors"
          >
            Yenidən yüklə
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// Scanning belongs to the door and the admin — on the event they are scanning
const ALLOWED_ROLES = [TICKET_CONTROL, EVENT_ADMIN];

// Visual config per validation outcome returned by the API.
const RESULT_STYLES = {
  valid: {
    ring: "ring-emerald-400",
    badge: "bg-emerald-500",
    icon: "✓",
    title: "Bilet etibarlıdır",
  },
  "already-used": {
    ring: "ring-amber-400",
    badge: "bg-amber-500",
    icon: "!",
    title: "Bilet artıq istifadə olunub",
  },
  // The overview QR of a table ticket: each guest has to show their own seat's QR
  "group-main": {
    ring: "ring-indigo-400",
    badge: "bg-indigo-500",
    icon: "i",
    title: "Masa bileti",
  },
  invalid: {
    ring: "ring-red-400",
    badge: "bg-red-500",
    icon: "✕",
    title: "Yanlış bilet",
  },
  forbidden: {
    ring: "ring-red-400",
    badge: "bg-red-500",
    icon: "✕",
    title: "İcazə yoxdur",
  },
  error: {
    ring: "ring-red-400",
    badge: "bg-red-500",
    icon: "✕",
    title: "Xəta baş verdi",
  },
};

const ScanPageInner = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // Where the admin came from (the specific event dashboard). Falls back to the
  // events list if the scanner was opened directly / after a refresh.
  const backTo = location.state?.from || "/dashboard";
  const auth = localStorage.getItem("auth");
  const decoded = useMemo(() => (auth ? decodeAuth() : null), [auth]);
  // Door staff have nowhere to go back to; anyone else came from a dashboard.
  // The role is the one they hold on the event they came from.
  const isDoorOnly = eventIdFromPath(backTo)
    ? roleFor(decoded, eventIdFromPath(backTo)) === TICKET_CONTROL
    : !hasRoleAnywhere(decoded, [EVENT_ADMIN, EVENT_VIEWER]);

  const isProcessingRef = useRef(false);
  const videoRef = useRef(null);

  const [phase, setPhase] = useState("scanning"); // scanning | validating | result
  const [result, setResult] = useState(null);
  const [cameraError, setCameraError] = useState(null);
  const [retryKey, setRetryKey] = useState(0);

  // Only ticket controllers / event admins may reach this page.
  useEffect(() => {
    if (!auth || !hasRoleAnywhere(decoded, ALLOWED_ROLES)) {
      window.location.replace("/");
    }
  }, [auth, decoded]);

  const handleDecoded = useCallback(async (decodedText) => {
    // The camera keeps firing this until it stops; only handle the first hit.
    if (isProcessingRef.current) return;
    isProcessingRef.current = true;

    setPhase("validating"); // triggers the effect cleanup, which stops the camera

    try {
      const { data } = await validateTicket(decodedText);
      setResult(data);
      setPhase("result");

      if (data?.success) {
        toast.success(data?.message || "Bilet etibarlıdır.");
      } else if (data?.status === "already-used" || data?.status === "group-main") {
        toast.warn(data?.message || "Bilet artıq istifadə olunub.");
      } else {
        toast.error(data?.message || "Yanlış bilet.");
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("Ticket validation request failed:", e);
      const msg =
        (typeof e?.response?.data?.message === "string" && e.response.data.message) ||
        "Server xətası. Yenidən cəhd edin.";
      setResult({ success: false, status: "error", message: msg });
      setPhase("result");
      toast.error(msg);
    }
  }, []);

  const rescan = useCallback(() => {
    isProcessingRef.current = false;
    setResult(null);
    setPhase("scanning");
  }, []);

  const retryCamera = useCallback(() => {
    setCameraError(null);
    setRetryKey((k) => k + 1);
  }, []);

  // Drive the camera with nimiq/qr-scanner: it prefers the native BarcodeDetector
  // and otherwise decodes with a WASM (ZBar) engine in a Web Worker, so decoding
  // runs off the main thread — far faster/more robust than a JS decoder, notably
  // on iOS. Each effect run owns one scanner; the `cancelled` guard covers the
  // race where start() resolves after cleanup (React StrictMode double-mount).
  useEffect(() => {
    if (phase !== "scanning") return undefined;
    const video = videoRef.current;
    if (!video) return undefined;

    let cancelled = false;
    setCameraError(null);

    const scanner = new QrScanner(
      video,
      (result) => handleDecoded(result?.data ?? result),
      {
        preferredCamera: "environment",
        maxScansPerSecond: 25,
        returnDetailedScanResult: true,
        // We render our own viewfinder overlay, so let the library size its scan
        // region but not draw on top of it.
        highlightScanRegion: false,
        highlightCodeOutline: false,
      }
    );

    scanner.start().then(
      () => {
        if (cancelled) {
          try { scanner.stop(); } catch (_) { /* noop */ }
        }
      },
      () => {
        if (!cancelled) {
          setCameraError(
            "Kameraya giriş mümkün olmadı. Zəhmət olmasa brauzerdə kamera icazəsini yoxlayın."
          );
        }
      }
    );

    return () => {
      cancelled = true;
      try { scanner.stop(); } catch (_) { /* noop */ }
      try { scanner.destroy(); } catch (_) { /* noop */ }
    };
  }, [phase, retryKey, handleDecoded]);

  const style = result ? RESULT_STYLES[result.status] || RESULT_STYLES.error : null;

  return (
    <div className="min-h-screen bg-surface-950 text-white flex flex-col">
      <ToastContainer position="top-center" autoClose={2500} theme="dark" />

      {/* Header */}
      <header className="sticky top-0 z-10 bg-surface-950/95 backdrop-blur border-b border-surface-800">
        <div className="flex items-center justify-between h-16 px-4 max-w-2xl mx-auto w-full">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="w-8 h-8 bg-gradient-to-br from-accent-500 to-pink-500 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-xs">T</span>
            </div>
            <span className="text-lg font-bold">
              Tixy<span className="text-accent-400">.az</span>
              <span className="hidden sm:inline text-surface-500 font-normal text-sm"> · Bilet yoxlama</span>
            </span>
          </Link>
          {isDoorOnly ? (
            <button
              onClick={() => navigate("/logout")}
              className="text-sm font-medium text-red-400 hover:text-red-300 px-3 py-1.5 rounded-lg hover:bg-surface-800 transition-colors"
            >
              🚪 Çıxış
            </button>
          ) : (
            <button
              onClick={() => navigate(backTo)}
              className="inline-flex items-center gap-1.5 text-sm font-medium text-white bg-white/10 border border-white/15 hover:bg-white/20 px-3 py-1.5 rounded-lg transition-colors"
            >
              ← Panelə qayıt
            </button>
          )}
        </div>
      </header>

      <main className="flex-1 flex flex-col items-center px-4 py-6 max-w-2xl mx-auto w-full">
        <div className="text-center mb-5">
          <h1 className="text-xl font-bold">Bilet QR kodunu skan edin</h1>
          <p className="text-surface-400 text-sm mt-1">
            Kameranı biletin QR koduna tutun — nəticə avtomatik göstəriləcək.
          </p>
        </div>

        {/* Camera / result stage */}
        <div className="relative w-full max-w-sm aspect-square rounded-3xl overflow-hidden bg-black border border-surface-800 shadow-2xl">
          {/* qr-scanner attaches the camera stream to this video element */}
          <video
            ref={videoRef}
            className="w-full h-full object-cover"
            muted
            playsInline
          />

          {/* Scanning guide overlay */}
          {phase === "scanning" && !cameraError && (
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
              <div className="w-3/4 aspect-square rounded-2xl border-2 border-white/70 shadow-[0_0_0_9999px_rgba(0,0,0,0.35)]" />
            </div>
          )}

          {/* Camera error */}
          {cameraError && (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-6 bg-surface-900">
              <div className="text-4xl mb-3">📷</div>
              <p className="text-sm text-surface-300 mb-4">{cameraError}</p>
              <button
                onClick={retryCamera}
                className="bg-accent-600 hover:bg-accent-700 text-white px-5 py-2.5 rounded-xl text-sm font-semibold transition-colors"
              >
                Yenidən cəhd et
              </button>
            </div>
          )}

          {/* Validating spinner */}
          {phase === "validating" && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-surface-900/90">
              <div className="w-10 h-10 border-4 border-white/20 border-t-accent-400 rounded-full animate-spin" />
              <p className="mt-4 text-sm text-surface-300">Yoxlanılır...</p>
            </div>
          )}

          {/* Result card */}
          {phase === "result" && result && (
            <div className={`absolute inset-0 flex flex-col items-center justify-center text-center p-6 bg-surface-900 ring-4 ${style.ring} ring-inset`}>
              <div className={`w-20 h-20 rounded-full ${style.badge} flex items-center justify-center text-4xl font-bold shadow-lg`}>
                {style.icon}
              </div>
              <h2 className="mt-4 text-lg font-bold">{style.title}</h2>
              {result.message && (
                <p className="text-sm text-surface-300 mt-1">{String(result.message)}</p>
              )}

              {result.ticket && typeof result.ticket === "object" && (
                <div className="mt-4 w-full text-left bg-surface-950/60 rounded-xl p-3 space-y-1 text-sm">
                  {result.ticket.name && (
                    <div className="flex justify-between gap-3">
                      <span className="text-surface-500">Ad</span>
                      <span className="font-medium text-right">{String(result.ticket.name)}</span>
                    </div>
                  )}
                  {result.ticket.ticketType && (
                    <div className="flex justify-between gap-3">
                      <span className="text-surface-500">Bilet növü</span>
                      <span className="font-medium text-right">
                        {String(typeof result.ticket.ticketType === "object" ? result.ticket.ticketType?.az || "" : result.ticket.ticketType)}
                      </span>
                    </div>
                  )}
                  {result.ticket.seat && (
                    <div className="flex justify-between gap-3">
                      <span className="text-surface-500">Yer</span>
                      <span className="font-medium text-right">{String(result.ticket.seat)}</span>
                    </div>
                  )}
                  {result.ticket.group && (
                    <div className="flex justify-between gap-3">
                      <span className="text-surface-500">Masadan daxil olub</span>
                      <span className="font-medium text-right">
                        {Number(result.ticket.group.entered) || 0}/{Number(result.ticket.group.total) || 0} nəfər
                      </span>
                    </div>
                  )}
                  {result.passedAt && (
                    <div className="flex justify-between gap-3">
                      <span className="text-surface-500">Keçid vaxtı</span>
                      <span className="font-medium text-right">{String(result.passedAt)}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="mt-6 w-full max-w-sm">
          {phase === "result" ? (
            <button
              onClick={rescan}
              className="w-full bg-accent-600 hover:bg-accent-700 text-white px-5 py-3.5 rounded-2xl text-base font-semibold transition-colors shadow-lg shadow-accent-600/25"
            >
              🔄 Növbəti bileti skan et
            </button>
          ) : (
            <p className="text-center text-surface-500 text-xs">
              {decoded?.email}
            </p>
          )}
        </div>
      </main>
    </div>
  );
};

const ScanPage = () => (
  <ScanErrorBoundary>
    <ScanPageInner />
  </ScanErrorBoundary>
);

export default ScanPage;
